/**
 * Coverage — Main coverage management page.
 * Sprint 16: ShiftCoverage
 *
 * Shows:
 * - Officers: Open coverage requests with review actions
 * - Members: Their own coverage requests and status
 * - Officer can tap into CoverageReviewPanel for detailed review
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { isOfficerOrAbove } from '../lib/auth';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useCoverage } from '../hooks/useCoverage';

import AppLayout from '../components/layout/AppLayout';
import CoverageReviewPanel from '../components/CoverageReviewPanel';
import CoverageChatDrawer from '../components/CoverageChatDrawer';

// -- Status config --

const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
  open: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)', label: 'Open' },
  officer_review: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.12)', label: 'Needs Review' },
  outreach: { color: '#D4A843', bg: 'rgba(212, 168, 67, 0.12)', label: 'Outreach' },
  filled: { color: '#22C55E', bg: 'rgba(34, 197, 94, 0.12)', label: 'Filled' },
  cancelled: { color: '#666', bg: 'rgba(102, 102, 102, 0.12)', label: 'Cancelled' },
};

// -- Helpers --

function formatShiftDate(dateStr: string | undefined): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// -- Component --

export default function Coverage() {
  const { user } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const isOfficer = isOfficerOrAbove(user);
  const { openRequests, myRequests, loading, error, fetchOpenRequests, fetchMyRequests } = useCoverage();

  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [tab, setTab] = useState<'open' | 'my'>(isOfficer ? 'open' : 'my');

  useEffect(() => {
    if (isOfficer) {
      fetchOpenRequests();
    }
    fetchMyRequests();
  }, []);

  const handleBack = () => {
    setSelectedRequestId(null);
    setChatOpen(false);
    // Refresh lists
    if (isOfficer) fetchOpenRequests();
    fetchMyRequests();
  };

  // Detail view
  if (selectedRequestId) {
    return (
      <AppLayout>
        <div style={{ padding: isDesktop ? '24px 32px' : '16px' }}>
          <button
            type="button"
            onClick={handleBack}
            style={{
              marginBottom: 16,
              padding: '6px 14px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: 6,
              color: '#999',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            ← Back to list
          </button>

          <CoverageReviewPanel
            requestId={selectedRequestId}
            onApproved={handleBack}
            onCancelled={handleBack}
            onOpenChat={() => setChatOpen(true)}
          />
        </div>

        <CoverageChatDrawer requestId={selectedRequestId} open={chatOpen} onClose={() => setChatOpen(false)} />
      </AppLayout>
    );
  }

  // List view
  const currentList = tab === 'open' ? openRequests : myRequests;

  return (
    <AppLayout>
      <div style={{ padding: isDesktop ? '24px 32px' : '16px', maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ color: '#D4A843', fontSize: 22, fontWeight: 700, margin: '0 0 20px' }}>Shift Coverage</h1>

        {/* Tabs */}
        {isOfficer && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
            {(['open', 'my'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  backgroundColor: tab === t ? 'rgba(212, 168, 67, 0.15)' : 'transparent',
                  color: tab === t ? '#D4A843' : '#888',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t === 'open' ? 'Open Requests' : 'My Requests'}
              </button>
            ))}
          </div>
        )}

        {loading && <div style={{ color: '#999', padding: 20, textAlign: 'center' }}>Loading...</div>}
        {error && <div style={{ color: '#C8322B', padding: 20, textAlign: 'center' }}>{error}</div>}

        {!loading && currentList.length === 0 && (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              color: '#666',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: 12,
            }}
          >
            {tab === 'open' ? 'No open coverage requests.' : 'You have no coverage requests.'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {currentList.map((req) => {
            const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.open;

            return (
              <button
                key={req.id}
                type="button"
                onClick={() => isOfficer && setSelectedRequestId(req.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: 16,
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: 10,
                  cursor: isOfficer ? 'pointer' : 'default',
                  transition: 'background-color 0.15s',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>
                    {req.battalion_name} — {formatShiftDate(req.shift_start)}
                  </span>
                  <span
                    style={{
                      padding: '3px 8px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      color: statusCfg.color,
                      backgroundColor: statusCfg.bg,
                    }}
                  >
                    {statusCfg.label}
                  </span>
                </div>
                <div style={{ color: '#999', fontSize: 13 }}>
                  {tab === 'open' && req.requesting_member_name && (
                    <span>Requested by: {req.requesting_member_name}</span>
                  )}
                  {tab === 'open' && req.candidate_count !== undefined && (
                    <span style={{ marginLeft: 12 }}>
                      {req.candidate_count} candidate{req.candidate_count !== 1 ? 's' : ''}
                    </span>
                  )}
                  {tab === 'my' && req.filled_by_name && <span>Covered by: {req.filled_by_name}</span>}
                  <span style={{ marginLeft: 12 }}>{formatTimestamp(req.created_at)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
